import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-tooltip/dist/react-tooltip.css";
import { NumericFormat } from "react-number-format";
import NoAttachmentWarning from "../../components/NoAttachmentWarning";
import Tour from "../../components/OnboardingTour/Tour";
import { useTranslation } from "react-i18next";
import { useEmissionCreateTour, menuName } from "./EmissionCreateTour";
import categoryInfo from "./category_source_links.json";
import { setFabMessage } from "../../features/layout/helpFabSlice";
import useShowPopup from "../../components/SimplePopUp";
import { Button, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  fetchCompanies,
  fetchCategories,
  fetchOffices,
  fetchEmissionFactors,
  createEmissionRecord,
} from "../../library/api/emission";
import FieldSkeleton from "../../components/PreloadWrappers/FieldSkeleton";
import PageHeader from "../../components/PageHeader";
import AttachmentDropZone from "../../components/AttachmentDropZone";

export default function EmissionCreate() {
  const { t, i18n } = useTranslation();
  const account = useSelector((s) => s.account.value);
  const tourFromDashboardRun = useSelector(
    (s) => s.tour.emissionTourFromDashboardRun
  );
  const dispatch = useDispatch();
  const showPopup = useShowPopup();

  const [redirectEmission, setRedirectEmission] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState(
    String(account?.client_id || "")
  );
  const [selectedOffice, setSelectedOffice] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedCategoryTooltip, setSelectedCategoryTooltip] = useState("");

  const [listCompany, setListCompany] = useState([]);
  const [listOffice, setListOffice] = useState([]);
  const [listCategory, setListCategory] = useState([]);
  const [listEmissionFactor, setListEmissionFactor] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [sourceInfo, setSourceInfo] = useState(null);

  // loading flags
  const [isLoadingInit, setIsLoadingInit] = useState(false);
  const [isLoadingOffices, setIsLoadingOffices] = useState(false);
  const [isLoadingFactors, setIsLoadingFactors] = useState(false);

  const [emissionRecord, setEmissionRecord] = useState({
    assets: "",
    factor: 0,
    amount: "",
    amount_2: 0,
    amount_3: 0,
    unit: 0,
    multiplier: 1,
  });

  const {
    tourSteps,
    tourRun,
    setTourRun,
    tourStepIndex,
    handleEnd,
    handleJoyrideCallback,
  } = useEmissionCreateTour({
    t,
    account,
    selectedCategory,
    listEmissionFactor,
    tourFromDashboardRun,
  });

  // Initial: companies + categories
  useEffect(() => {
    (async () => {
      try {
        setIsLoadingInit(true);
        const [companies, categories] = await Promise.all([
          fetchCompanies(),
          fetchCategories([1, 2], String(account?.client_id || "")),
        ]);
        setListCompany(companies || []);
        setListCategory(categories || []);
      } finally {
        setIsLoadingInit(false);
      }
    })();
  }, [account?.client_id]);

  const onSelectCompany = async (e) => {
    const companyId = Number(e.target.value) || 0;
    setSelectedCompany(companyId);

    const selectedCompanyOption = (listCompany || []).find(
      (item) => Number(item.company_id) === companyId
    );
    const nextClientId = selectedCompanyOption?.company_client
      ? String(selectedCompanyOption.company_client)
      : "";

    setSelectedClientId(nextClientId);
    setSelectedCategory(0);
    setListEmissionFactor([]);
    setEmissionRecord((prev) => ({
      ...prev,
      factor: 0,
      unit: 0,
      multiplier: 1,
      amount: "",
      amount_2: 0,
      amount_3: 0,
    }));
    setSelectedFile(null);

    try {
      setIsLoadingInit(true);
      const categories = await fetchCategories([1, 2], nextClientId);
      setListCategory(categories || []);
    } finally {
      setIsLoadingInit(false);
    }
  };

  // Offices when company changes
  useEffect(() => {
    (async () => {
      if (!selectedCompany) {
        setListOffice([]);
        setSelectedOffice(0);
        return;
      }
      try {
        setIsLoadingOffices(true);
        const offices = await fetchOffices(selectedCompany);
        setListOffice(offices || []);
        setSelectedOffice(0);
      } catch {
        setListOffice([]);
        setSelectedOffice(0);
      } finally {
        setIsLoadingOffices(false);
      }
    })();
  }, [selectedCompany]);

  // Emission factors when category changes
  useEffect(() => {
    (async () => {
      if (!selectedCategory) {
        setListEmissionFactor([]);
        return;
      }
      try {
        setIsLoadingFactors(true);
        const factors = await fetchEmissionFactors(
          selectedCategory,
          selectedClientId
        );
        const factorList = Array.isArray(factors) ? factors : [];

        const normalizedFactors = factorList.map((factor) => {
          const name = typeof factor?.name === "string" ? factor.name.trim() : "";
          const year = factor?.year;
          const hasYear = year !== undefined && year !== null && year !== "";

          if (!name || !hasYear) return factor;

          return {
            ...factor,
            name: `${name} (${year})`,
          };
        });

        normalizedFactors.sort((a, b) =>
          String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, {
            sensitivity: "base",
            numeric: true,
          })
        );

        setListEmissionFactor(normalizedFactors);
      } catch {
        setListEmissionFactor([]);
      } finally {
        setIsLoadingFactors(false);
      }
    })();
  }, [selectedCategory, selectedClientId]);

  useEffect(() => {
    if (!selectedCategory) {
      setSelectedCategoryTooltip("");
      return;
    }
    const cat = (listCategory || []).find(
      (c) => Number(c.id) === Number(selectedCategory)
    );
    if (!cat) {
      setSelectedCategoryTooltip("");
      return;
    }
    const tip = cat.description || cat.descriptionId || "";
    setSelectedCategoryTooltip(tip);
  }, [selectedCategory, listCategory, i18n.language]);

  const onSelectCategory = (e) => {
    const categoryValue = Number(e.target.value) || 0;

    const meta = categoryInfo[String(categoryValue)];
    const nextSource = meta?.source || categoryInfo.default?.source || null;
    const nextAd = meta?.ad ?? null;
    setSourceInfo(categoryValue > 0 ? nextSource : null);

    dispatch(
      setFabMessage({
        title: "More with TruCarbon",
        message: nextAd,
        whatsappText:
          "Hello TruCarbon, I want to learn more about TruCarbon's services.",
      })
    );

    setSelectedCategory(categoryValue);
    setEmissionRecord({
      assets: "",
      factor: 0,
      amount: "",
      amount_2: 0,
      amount_3: 0,
      unit: 0,
      multiplier: 1,
    });
    setSelectedFile(null);
  };

  const handleFactorClick = () => {
    dispatch(
      setFabMessage({
        title: "setting.emission.custom.title",
        message: "setting.emission.custom.hook",
        whatsappText: "Hello TruCarbon, I need customized emission factors.",
      })
    );
  };

  const onSelectEmission = (e) => {
    const val = Number(e.target.value) || 0;
    setEmissionRecord((prev) => ({
      ...prev,
      factor: val,
      unit: 0,
      multiplier: 1,
      amount: "",
      amount_2: 0,
      amount_3: 0,
    }));
  };

  const onSelectUnit = (e) => {
    const val = Number(e.target.value);
    const currentFactor = listEmissionFactor.find(
      (f) => Number(f.id) === Number(emissionRecord.factor)
    );
    const unitObj = currentFactor?.units?.find((u) => Number(u.id) === val);
    setEmissionRecord((prev) => ({
      ...prev,
      unit: val,
      multiplier: unitObj?.multiplier ?? 1,
    }));
  };

  const onAmountChange = (values) => {
    setEmissionRecord((prev) => ({ ...prev, amount: values?.value ?? "" }));
  };

  const onInletOutletCODChange = (values, sourceInfo) => {
    const key = sourceInfo?.event?.target?.name;
    if (key)
      setEmissionRecord((prev) => ({ ...prev, [key]: values?.value ?? "" }));
  };

  const onSaveRecord = async () => {
    setIsSaving(true);
    try {
      const selectedCat = (listCategory || []).find(
        (c) => Number(c.id) === Number(selectedCategory)
      );
      if (!selectedCat) throw new Error("Invalid category");

      const payload = {
        scope: selectedCat.scope,
        category: selectedCategory,
        factor: emissionRecord.factor,
        unit: emissionRecord.unit,
        clientId: selectedClientId,
        company: selectedCompany,
        location: selectedOffice,
        assets: emissionRecord.assets,
        date: selectedDate.toISOString(),
        amount: emissionRecord.amount,
      };

      if (Number(selectedCategory) === 26) {
        payload.amount_2 = emissionRecord.amount_2 || 0;
        payload.amount_3 = emissionRecord.amount_3 || 0;
      }

      const ok = await createEmissionRecord(payload, selectedFile);
      if (!ok) throw new Error("Save failed");

      await showPopup({
        type: "success",
        html: "<p>Successfully record emissions</p>",
        confirmButtonText: "Ok",
      });
      setRedirectEmission(true);
    } catch {
      await showPopup({
        type: "warning",
        html: "<p>Please fill all information needs</p>",
        confirmButtonText: "Ok",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (redirectEmission) {
    return <Navigate to="/emission" />;
  }

  const currentFactor = listEmissionFactor.find(
    (f) => Number(f.id) === Number(emissionRecord.factor)
  );
  const currentUnits = currentFactor?.units || [];
  const selectedUnit = currentUnits.find(
    (u) => Number(u.id) === Number(emissionRecord.unit)
  );

  const typeLabel = (() => {
    const c = Number(selectedCategory);
    if (c === 3) return t("emission.form.label9");
    if (c === 4 || c === 26) return t("emission.form.label19");
    if (c === 5) return t("emission.form.label26");
    if (c === 6) return t("emission.form.label25");
    if (c === 7) return t("emission.form.label8");
    if (c === 12) return t("emission.form.label27");
    if (c === 13) return t("emission.form.label28");
    if (c === 18) return t("emission.form.label22");
    if ([20, 21, 22].includes(c)) return t("emission.form.label21");
    if (c === 25) return t("emission.form.mineDepth");
    return t("emission.form.label10");
  })();

  const amountLabel = (() => {
    const c = Number(selectedCategory);
    if (c === 4) return t("emission.form.label20");
    if (c === 5) return t("emission.form.label12");
    return t("emission.form.label13");
  })();

  return (
    <div className="page-content">
      <div className="container-fluid">
        <PageHeader
          title={t("general.create")}
          // subtitle={t("emission.form.subtitle")}
          actions={
            <Button variant="contained" onClick={() => setTourRun(true)}>
              {t("dashboard.startTour")}
            </Button>
          }
          labels={{
            emission: t("menu.label21"),
            create: "Scope 1 & 2",
          }}
          linkLast={false}
          home={true}
        />

        <Tour
          run={tourRun}
          onEnd={handleEnd}
          steps={tourSteps}
          menu={menuName}
          callback={handleJoyrideCallback}
          stepIndex={tourStepIndex}
          scrollToFirstStep
        />

        <div className="card">
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <div className="mb-3" id="tour__company">
                  <label htmlFor="company" className="form-label">
                    {t("emission.form.label1")}
                  </label>
                  <FieldSkeleton loading={isLoadingInit}>
                    <select
                      onChange={onSelectCompany}
                      value={selectedCompany}
                      id="company"
                      name="company"
                      className="form-select mb-3"
                    >
                      {!selectedCompany && (
                        <option value="0">
                          {t("emission.form.placeholder1")}
                        </option>
                      )}
                      {listCompany.map((item) => (
                        <option key={item.company_id} value={item.company_id}>
                          {item.company_name}
                        </option>
                      ))}
                    </select>
                  </FieldSkeleton>
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3" id={selectedCompany ? "tour__location" : "location"}
                >
                  <label htmlFor="office" className="form-label">
                    {t("emission.table.label2")}
                  </label>
                  <FieldSkeleton loading={isLoadingOffices}>
                    <select
                      onChange={(e) =>
                        setSelectedOffice(Number(e.target.value) || 0)
                      }
                      value={selectedOffice}
                      id="office"
                      name="office"
                      className="form-select mb-3"
                      disabled={!selectedCompany}
                    >
                      {!selectedOffice && (
                        <option value="0">
                          {t("emission.form.placeholder2")}
                        </option>
                      )}
                      {listOffice.map((item) => (
                        <option key={item.office_id} value={item.office_id}>
                          {item.office_name}
                          {item.office_address
                            ? ` (${item.office_address})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </FieldSkeleton>
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3" id={selectedOffice ? "tour__month_and_year" : "month_and_year"}
                >
                  <label htmlFor="date" className="form-label">
                    {t("emission.form.label4")}
                  </label>
                  <FieldSkeleton loading={isLoadingInit}>
                    <DatePicker
                      id="date"
                      selected={selectedDate}
                      onChange={(d) =>
                        setSelectedDate(
                          new Date(
                            new Date(d).getFullYear(),
                            new Date(d).getMonth(),
                            7
                          )
                        )
                      }
                      dateFormat="MMMM yyyy"
                      className="form-select mb-3"
                      showMonthYearPicker
                      popperClassName="trucount-popper-date-picker-class"
                      popperPlacement={tourRun ? "top" : "bottom"}
                    />
                  </FieldSkeleton>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="mb-3" id="tour__emission_source">
                  <label htmlFor="category" className="form-label">
                    {t("emission.form.label2")}
                  </label>
                  <FieldSkeleton loading={isLoadingInit}>
                    <>
                      <div className="input-group">
                        <select
                          onChange={onSelectCategory}
                          value={selectedCategory}
                          id="category"
                          name="category"
                          className="form-select"
                        >
                          <option value="0">
                            {t("emission.form.placeholder3")}
                          </option>
                          {listCategory.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name || item.nameId || ""}
                            </option>
                          ))}
                        </select>
                        <span
                          className="input-group-text"
                          id="basic-addon2"
                          title={selectedCategoryTooltip}
                        >
                          <i className="ri-information-line"></i>
                        </span>
                      </div>

                      {sourceInfo && (
                        <p className="mt-2">
                          &nbsp;{t("emission.form.label3")} :
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href={sourceInfo.href}
                          >
                            &nbsp;{sourceInfo.text}
                          </a>
                        </p>
                      )}
                    </>
                  </FieldSkeleton>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3" id={emissionRecord.assets !== "" ? "tour__type" : "emission_source"}>
                  <label htmlFor="factor" className="form-label">
                    {typeLabel}
                  </label>
                  <FieldSkeleton loading={isLoadingFactors}>
                    <>
                      <select
                        id="factor"
                        onClick={handleFactorClick}
                        onChange={onSelectEmission}
                        value={emissionRecord.factor || 0}
                        className="form-select mb-0"
                        disabled={!selectedCategory}
                      >
                        <option value="0">
                          {t("emission.form.select")} {typeLabel}
                        </option>
                        {listEmissionFactor.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>

                      {!!selectedCategory &&
                        listEmissionFactor.length === 0 &&
                        !isLoadingFactors && (
                          <p className="mt-2">
                            No emission factors available for this category
                          </p>
                        )}
                    </>
                  </FieldSkeleton>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3" id={emissionRecord.factor > 0 ? "tour__unit" : "unit"}>
                  <label htmlFor="unit" className="form-label">
                    {t("emission.form.label11")}
                  </label>
                  <FieldSkeleton loading={isLoadingFactors}>
                    <select
                      id="unit"
                      onChange={onSelectUnit}
                      value={emissionRecord.unit || 0}
                      className="form-select mb-0"
                      disabled={!emissionRecord.factor}
                    >
                      <option value="0">
                        {t("emission.form.placeholder4")}
                      </option>
                      {currentUnits?.length > 0 ? (
                        currentUnits.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))
                      ) : (
                        <option value="0" disabled>
                          {"No units available for this factor"}
                        </option>
                      )}
                    </select>
                  </FieldSkeleton>
                </div>
              </div>
            </div>

            {Number(selectedCategory) === 26 && (
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3" id="tour__inletCOD">
                    <label htmlFor="amount_2" className="form-label">
                      {t("emission.form.inletCOD")}
                    </label>
                    <NumericFormat
                      id="amount_2"
                      name="amount_2"
                      className="form-control"
                      value={emissionRecord.amount_2 || ""}
                      onValueChange={onInletOutletCODChange}
                      decimalSeparator={i18n.language === "id" ? "," : "."}
                      thousandSeparator={i18n.language === "id" ? "." : ","}
                      placeholder={t("emission.form.enter")}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3" id="tour__outletCOD">
                    <label htmlFor="amount_3" className="form-label">
                      {t("emission.form.outletCOD")}
                    </label>
                    <NumericFormat
                      id="amount_3"
                      name="amount_3"
                      className="form-control"
                      value={emissionRecord.amount_3 || ""}
                      onValueChange={onInletOutletCODChange}
                      decimalSeparator={i18n.language === "id" ? "," : "."}
                      thousandSeparator={i18n.language === "id" ? "." : ","}
                      placeholder={t("emission.form.enter")}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3" id={selectedCategory ? "tour__asset_name" : "asset_name"}>
                  <label htmlFor="asset" className="form-label">
                    {t("emission.form.label7")}
                  </label>
                  <input
                    id="asset"
                    onChange={(e) =>
                      setEmissionRecord((p) => ({
                        ...p,
                        assets: e.target.value,
                      }))
                    }
                    value={emissionRecord.assets || ""}
                    className="form-control"
                    placeholder={t("emission.form.enter")}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-3" id={selectedUnit ? "tour__amount" : "amount"}>
                  <label htmlFor="amount" className="form-label">
                    {amountLabel}
                  </label>

                  <div className="input-group">
                    <NumericFormat
                      id="amount"
                      name="amount"
                      className="form-control"
                      value={emissionRecord.amount || ""}
                      onValueChange={onAmountChange}
                      decimalSeparator={i18n.language === "id" ? "," : "."}
                      thousandSeparator={i18n.language === "id" ? "." : ","}
                      placeholder={t("emission.form.enter")}
                      allowNegative={false}
                      isAllowed={(vals) => (vals.floatValue ?? 0) >= 0}
                      inputMode="decimal"
                      disabled={!emissionRecord.unit}
                    />

                    <span
                      className="input-group-text"
                      title={
                        selectedUnit
                          ? `${selectedUnit.name} : ""}`
                          : t("emission.form.selectUnitFirst") ||
                          "Select unit first"
                      }
                      aria-live="polite"
                    >
                      {selectedUnit ? selectedUnit.name : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Attachment */}
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3" id={emissionRecord.amount > 0 ? "tour__attachment" : "attachment"}>
                  <label
                    htmlFor="formFile"
                    className="form-label d-inline-flex align-items-center"
                  >
                    {t("emission.form.label15")}
                    <Tooltip
                      title={<NoAttachmentWarning />}
                      arrow
                      placement="top"
                      enterDelay={120}
                      leaveDelay={60}
                      disableInteractive={false}
                      PopperProps={{
                        modifiers: [
                          { name: "offset", options: { offset: [0, 8] } },
                        ],
                      }}
                      slotProps={{
                        tooltip: {
                          sx: {
                            p: 0,
                            bgcolor: "background.paper",
                            boxShadow: 3,
                            borderRadius: 1,
                            maxWidth: 500,
                          },
                        },
                        arrow: { sx: { color: "background.paper" } },
                      }}
                    >
                      <span
                        className="d-inline-flex align-items-center"
                        tabIndex={0}
                      >
                        <IconButton
                          size="small"
                          aria-label={t("general.info") || "Info"}
                          sx={{
                            ml: 0.5,
                            opacity: 0.8,
                            "&:hover": { opacity: 1 },
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </label>
                  <p className="text-muted">
                    - {t("emission.form.label24")}
                    <br />- {t("emission.form.label16")}
                  </p>

                  <AttachmentDropZone
                    id="formFile"
                    file={selectedFile}
                    onFileSelect={(f) => setSelectedFile(f)}
                    accept="image/*,.pdf,.doc,.docx"
                  />
                </div>
              </div>
            </div>

            {/* Save */}
            <div className="row">
              <div className="col-lg-12">
                <div className="text-start">
                  <button
                    onClick={onSaveRecord}
                    type="button"
                    className="btn btn-primary"
                    id="tour__save"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        {t("general.btnSaving")}
                      </>
                    ) : (
                      t("general.btnSave")
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
